// Implements: F-UIX-019
/**
 * How the status panel writes its figures (UIX.8, OBS.12).
 *
 * Every date, time and percentage is written in the platform locale whatever the browser's
 * ("13/10/26, 15:00", never "10/13/26, 3:00 PM"; `types/locale.ts`), because it sits inside
 * Spanish copy. An instant (a job's last run, the probe's check, the read itself) is a
 * moment, so it is written in the reader's ZONE. The alert's month (`YYYY-MM`) is a calendar month of the
 * server's window, not a moment: it is written at UTC, so no browser zone moves it to the
 * month before, and in Spanish, because it sits inside a Spanish sentence. Whatever does not
 * parse is shown as it came, never guessed.
 *
 * "Behind" is measured against the server's own `read_at`, never the browser's clock: the
 * panel needs no clock of its own, so two readers of one answer see the same verdict.
 */

import { PLATFORM_HOUR_CYCLE, PLATFORM_LOCALE } from "../../../types/locale";
import type { JobStatusView } from "../../../types/status";

const INSTANT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  dateStyle: "short",
  timeStyle: "short",
  hourCycle: PLATFORM_HOUR_CYCLE,
});
const MONTH = new Intl.DateTimeFormat(PLATFORM_LOCALE, { month: "long", year: "numeric", timeZone: "UTC" });
const PERCENT = new Intl.NumberFormat(PLATFORM_LOCALE, { style: "percent", maximumFractionDigits: 2 });
const MONTH_RE = /^(\d{4})-(\d{2})$/;

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The instant in the reader's zone; `null` stays `null`, an unreadable text is shown as it came. */
export function instantText(iso: string | null): string | null {
  if (iso === null) return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? iso : INSTANT.format(at);
}

/** An age or interval in whole seconds, in its largest two units ("1 h 5 min", "45 s"). */
export function durationText(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const units: readonly (readonly [number, string])[] = [
    [DAY, "d"],
    [HOUR, "h"],
    [MINUTE, "min"],
    [1, "s"],
  ];
  const index = units.findIndex(([size]) => whole >= size);
  if (index === -1) return "0 s";
  const parts: string[] = [];
  let rest = whole;
  for (const [size, unit] of units.slice(index, index + 2)) {
    const amount = Math.floor(rest / size);
    rest -= amount * size;
    if (amount > 0) parts.push(`${String(amount)} ${unit}`);
  }
  return parts.join(" ");
}

/**
 * A job is behind when a whole interval has passed since the run it was due: the jobs role
 * stopped or is not picking it up. A failing job is NOT behind: the scheduler moves
 * `last_run_at` and `next_run_at` after a failed handler too, and the payload carries no
 * last outcome. Judged against the server's `read_at`; with no due run or no reading it is
 * not called behind.
 */
export function isBehind(job: JobStatusView, readAt: string | null): boolean {
  if (job.next_run_at === null || readAt === null) return false;
  const due = Date.parse(job.next_run_at);
  const read = Date.parse(readAt);
  if (Number.isNaN(due) || Number.isNaN(read)) return false;
  return read - due > job.interval_seconds * 1000;
}

/** The alert's calendar month ("octubre de 2026"), never moved by the reader's zone. */
export function monthText(month: string): string {
  const match = MONTH_RE.exec(month);
  if (match === null) return month;
  const index = Number(match[2]) - 1;
  if (index < 0 || index > 11) return month;
  return MONTH.format(Date.UTC(Number(match[1]), index, 1));
}

/** The failed-step rate (0..1) as a percentage. */
export function rateText(rate: number): string {
  return PERCENT.format(rate);
}
